import Product, { ProductType } from '@/models/Product/Product'
import PriceOperationPolicy from '@/models/Cart/PriceOperationPolicy'
import API, { initApiUrl } from '@/store/api'
import { isProvideImmediateDiscount } from '../../config/featureProvider'
import ResultSet from '../../models/Common/ResultSet'

initApiUrl('')

const cart = {
  namespaced: true,
  state: {
    purchaseableProducts: [],
    unpurchasableProducts: [],
    priceOperationPolicy: {},
    totalPrice: 0
  },
  mutations: {
    setPurchaseableProducts: (state, products) => {
      state.purchaseableProducts = products.map(product => new Product(product))
    },
    setUnpurchasableProducts: (state, products) => {
      state.unpurchasableProducts = products.map(product => new Product(product, true))
    },
    setPriceOperationPolicy: (state, priceOperationPolicy) => {
      state.priceOperationPolicy = new PriceOperationPolicy(priceOperationPolicy)
    },
    setTotalPrice: (state, totalPrice) => {
      state.totalPrice = totalPrice
    },

    selectAllProducts: (state, checked) => {
      state.purchaseableProducts.forEach(product => {
        if (product.getType() <= ProductType.SINGLE) product.selectItems(checked)
        else product.select(checked)
      })
    },
    removeProduct(state, productId) {
      const product = state.purchaseableProducts
      const index = product.findIndex(base => base.id === productId)

      if (index > -1) product.splice(index, 1)
    },
    removeItem(state, ids) {
      const product = state.purchaseableProducts
      const productIdx = product.findIndex(base => base.getId() === ids.productId)
      if (productIdx === -1) return

      const itemIdx = product[productIdx].getItems().findIndex(base => base.getId() === ids.itemId)
      product[productIdx].getItems().splice(itemIdx, 1)

      if (product[productIdx].getItems().length === 0)
        state.purchaseableProducts.splice(productIdx, 1)
    },
    removeUnpurchasableProduct(state, productId) {
      const product = state.unpurchasableProducts
      const index = product.findIndex(base => base.getId() === productId)

      if (index > -1) product.splice(index, 1)
    },
    removeUnpurchasableItem(state, ids) {
      const product = state.unpurchasableProducts
      const productIdx = product.findIndex(base => base.getId() === ids.productId)
      if (productIdx === -1) return

      const itemIdx = product[productIdx].items.findIndex(base => base.getId() === ids.itemId)
      product[productIdx].items.splice(itemIdx, 1)

      if (product[productIdx].items.length === 0) state.unpurchasableProducts.splice(productIdx, 1)
    }
  },
  getters: {
    cartProducts: state =>
      state.purchaseableProducts.filter(product => product.getType() <= ProductType.SINGLE),
    cartSetProducts: state =>
      state.purchaseableProducts.filter(product => product.getType() > ProductType.SINGLE),
    purchaseableProducts: state => state.purchaseableProducts,
    unpurchasableProducts: state => state.unpurchasableProducts,
    checkedCartProducts: state =>
      state.purchaseableProducts
        .filter(product =>
          product.getType() > ProductType.SINGLE
            ? product.getChecked()
            : product.items.some(item => item.getChecked())
        )
        .map(product =>
          product.getType() > ProductType.SINGLE
            ? product
            : new Product({
                productId: product.getId(),
                items: product.getItems().filter(item => item.getChecked()),
                options: product.getOptions()
              })
        ),

    minimumOrderPrice: state => state.priceOperationPolicy.minimumOrderPrice,
    isReturnAndExchangeItemInCart: state =>
      state.purchaseableProducts
        .filter(product => product.getChecked() && product.getType() === ProductType.SINGLE)
        .filter(product => product.getReturnOrExchange())
        .some(product => product.getItems().some(item => item.getChecked())),
    cartDiscounts: state => state.priceOperationPolicy.discounts,
    cartDeliveryCharges: state => state.priceOperationPolicy.deliveryCharges,

    cartReverseDeliveryCharges: state => state.priceOperationPolicy.reverseDeliveryCharges,
    nowCartDiscount: state => nowPrice =>
      state.priceOperationPolicy.discounts.reduce((iter, discount) => {
        if (discount.getCause().getStartAmount() <= nowPrice) iter = discount

        return iter
      }, null),

    nowCartToolTip: state => nowPrice =>
      state.priceOperationPolicy.discounts.reduce((iter, discount) => {
        if (
          discount.getTooltip().getGreaterThanOrEqual() <= nowPrice &&
          nowPrice < discount.getTooltip().getLessThan()
        ) {
          iter = discount
        }

        return iter
      }, null),

    nowCartDeliveryCharge: state => nowPrice =>
      state.priceOperationPolicy.deliveryCharges.reduce((iter, charge) => {
        if (charge.getCause().getStartAmount() <= nowPrice) iter = charge

        return iter
      }, null),

    defaultDeliveryCharge: state => state.priceOperationPolicy.defaultDeliveryCharge,

    totalPrice: (state, getters) => {
      const extractPrice = (iter, target) => {
        if (!target.getChecked()) return iter

        iter += target.getQuantity() * target.getSalePrice()
        return iter
      }

      let result = 0

      getters.cartProducts.forEach(product => {
        result += product.getItems().reduce(extractPrice, 0)
      })

      result += getters.cartSetProducts.reduce(extractPrice, 0)
      return result
    },
    totalQuantity: (state, getters) => {
      const extractQuantity = (iter, target) => {
        if (!target.getChecked()) return iter

        iter += target.getQuantity()
        return iter
      }

      let result = 0

      getters.cartProducts.forEach(product => {
        result += product.getItems().reduce(extractQuantity, 0)
      })

      result += getters.cartSetProducts.reduce(extractQuantity, 0)

      return result
    },
    isEmptySelectProduct: (state, getters) => {
      let checkedItem = true

      const count = getters.cartProducts.reduce((iter, product) => {
        if (product.stockCount < product.quantity) return iter
        product.getItems().forEach(item => {
          if (item.getChecked()) iter += 1
        })

        return iter
      }, 0)

      if (count > 0) {
        checkedItem = getters.cartProducts.some(product =>
          product.getItems().some(item => item.getChecked())
        )
        return !checkedItem
      }

      checkedItem = getters.cartSetProducts.some(product => product.getChecked())
      return !checkedItem
    },
    isAllSelectProduct: (state, getters) => {
      let checkedItem = true

      const count = getters.cartProducts.reduce((iter, product) => {
        if (product.stockCount < product.quantity) return iter

        product.getItems().forEach(item => {
          if (!item.getChecked()) iter += 1
        })

        return iter
      }, 0)

      if (count > 0) {
        checkedItem = getters.cartProducts.some(product =>
          product.getItems().some(item => !item.getChecked())
        )
        return !checkedItem
      }

      checkedItem = getters.cartSetProducts.some(product => !product.getChecked())
      return !checkedItem
    },
    isEmptyProduct: (state, getters) =>
      getters.cartProducts.length === 0 && getters.cartSetProducts.length === 0,
    isEmptyImmediateDiscount: state => state.priceOperationPolicy.discounts.length === 0
  },
  actions: {
    load: async ({ commit, rootState }) => {
      const option = {
        headers: {
          'user-baedal': rootState.user.authorization.key,
          'SESSION-ID': rootState.user.authorization.sessionId,
          Authorization: rootState.user.authorization.accessToken
        },
        params: { baeminShopNo: rootState.user.baeminShopNo }
      }

      const { failed, message, result } = await API.get('/v2/cart', option)
      if (failed) {
        return ResultSet.fail({
          message: message || '주문 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.'
        })
      }

      commit('setPurchaseableProducts', result.purchaseableProducts)
      commit('setUnpurchasableProducts', result.unpurchasableProducts)
      commit('setPriceOperationPolicy', {
        ...result.priceOperationPolicy,
        deliveryCharge: result.priceOperationPolicy.deliveryCharges
      })
      commit('setTotalPrice', result.totalPrice)

      return ResultSet.ok({ result })
    },

    modifyQuantity: async ({ rootState }, { product, value }) => {
      try {
        const items = product.getItems().map(item => ({
          itemId: item.getItemId(),
          quantity: value
        }))

        const result = await API.patch(
          '/v2/cart',
          {
            productId: product.getId(),
            items,
            referrer: {
              ScreenName: 'Cart'
            },
            baeminShopNo: rootState.user.baeminShopNo
          },
          {
            headers: {
              'user-baedal': rootState.user.authorization.key,
              'SESSION-ID': rootState.user.authorization.sessionId,
              Authorization: rootState.user.authorization.accessToken
            }
          }
        )

        if (result.code !== '200') return false

        return true
      } catch (err) {
        return false
      }
    },

    removeProducts: async ({ rootState }, products) => {
      try {
        const items = products.reduce((iter, product) => {
          product.getItems().forEach(item => {
            const newItem = {
              itemId: item.getItemId(),
              productId: product.getId(),
              state: item.state
            }
            iter.push(newItem)
          })

          return iter
        }, [])

        const result = await API.delete('/v2/cart', {
          data: {
            items
          },
          params: {
            baeminShopNo: rootState.user.baeminShopNo
          },
          headers: {
            'user-baedal': rootState.user.authorization.key,
            'SESSION-ID': rootState.user.authorization.sessionId,
            Authorization: rootState.user.authorization.accessToken
          }
        })

        if (result.code !== '200') return false

        return true
      } catch (err) {
        console.log(err)
        return false
      }
    },

    paymentToCartProducts: async ({ rootState, getters }, products) => {
      try {
        const newProducts = products.map(product => {
          const items = product.items.map(item => ({
            baeminShopNo: rootState.user.baeminShopNo,
            cartItemId: item.getId(),
            itemId: item.getItemId(),
            quantity:
              product.getType() > ProductType.SINGLE ? product.getQuantity() : item.getQuantity(),
            quantityPrice:
              product.getType() > ProductType.SINGLE
                ? item.getSalePrice() * product.getQuantity()
                : item.getSalePrice() * item.getQuantity(),
            supplyPrice: item.getSupplyPrice(),
            unitPrice: item.getSalePrice()
          }))

          const options = product.options
            .filter(option => option.nowCount > 0)
            .map(option => ({
              itemId: option.itemId,
              quantity: option.nowCount
            }))

          return {
            baeminShopNo: rootState.user.baeminShopNo,
            productId: product.getId(),
            items,
            options
          }
        })

        const provideImmediateDiscount = isProvideImmediateDiscount({
          appVersion: rootState.user.appVersion,
          isAndroid: rootState.common.isAndroid
        })

        const nowDiscount = getters.nowCartDiscount(getters.totalPrice)
        const nowDeliveryCharge = getters.nowCartDeliveryCharge(getters.totalPrice)

        let cartDiscount = null
        let cartDeliveryTip = null

        if (provideImmediateDiscount && nowDiscount) {
          cartDiscount = {
            amount: nowDiscount.getDiscountAmount(),
            id: nowDiscount.getId()
          }
        }

        if (nowDeliveryCharge) {
          cartDeliveryTip = {
            amount: nowDeliveryCharge.getDeliveryCharge(),
            id: nowDeliveryCharge.getId(),
            deliveryChargeBeforeDiscount: nowDeliveryCharge.getDeliveryChargeBeforeDiscount()
          }
        }

        const result = await API.post(
          '/v1/orders/sheet',
          getters.isReturnAndExchangeItemInCart
            ? { products: newProducts, cartDiscount: null, cartDeliveryTip: null }
            : { products: newProducts, cartDiscount, cartDeliveryTip },
          {
            headers: {
              'user-baedal': rootState.user.authorization.key,
              'SESSION-ID': rootState.user.authorization.sessionId,
              Authorization: `Bearer ${rootState.user.authorization.accessToken}`
            }
          }
        )

        if (result.code !== '200') return result

        return result
      } catch (err) {
        return err.response.data
      }
    },

    /**
     * @returns {boolean|string|null} false: 성공, string: validate error message, null: 서버 에러
     */
    validateEqualPriceOperationPolicyToBeforePayment: async ({ getters, rootState }) => {
      try {
        const option = {
          headers: {
            'user-baedal': rootState.user.authorization.key,
            'SESSION-ID': rootState.user.authorization.sessionId,
            Authorization: `Bearer ${rootState.user.authorization.accessToken}`
          },
          params: { baeminShopNo: rootState.user.baeminShopNo }
        }

        const priceOperationPolicyData = await API.get('/v2/cart/price/operation', option)
        if (!priceOperationPolicyData.success) return null

        const normalizeObject = (iter, item) => {
          if (typeof item === 'object') {
            Object.values(item).forEach(data => {
              normalizeObject(iter, data)
            })

            return iter
          }

          iter.push(item)
          return iter
        }

        const priceOperationPolicy = new PriceOperationPolicy(priceOperationPolicyData.result)

        // stage 1 :: discount 비교
        const discountEqualResult = () => {
          const result = priceOperationPolicy.discounts.reduce((iter, discount) => {
            iter = normalizeObject(iter, discount)
            return iter
          }, [])

          const beforeDiscounts = getters.cartDiscounts.reduce((iter, discount) => {
            iter = normalizeObject(iter, discount)
            return iter
          }, [])

          // True 면 이상한 것
          return result.some((discount, index) => discount !== beforeDiscounts[index])
        }

        // stage 2 :: DeliveryCharge 비교
        const deliveryChargeEqualResult = () => {
          const result = priceOperationPolicy.deliveryCharges.reduce((iter, charge) => {
            iter = normalizeObject(iter, charge)
            return iter
          }, [])

          const beforeCharges = getters.cartDeliveryCharges.reduce((iter, charge) => {
            iter = normalizeObject(iter, charge)
            return iter
          }, [])

          return result.some((discount, index) => discount !== beforeCharges[index])
        }

        if (discountEqualResult() && deliveryChargeEqualResult())
          return '즉시할인 정보가 변경되었습니다. 확인 후 주문해 주세요.'
        if (discountEqualResult()) return '즉시할인 정보가 변경되었습니다. 확인 후 주문해 주세요.'
        if (deliveryChargeEqualResult())
          return '배달팁 정보가 변경되었습니다. 확인 후 주문해 주세요.'

        return false

        // 둘 중 하나라도 이상하면 true 배포
      } catch (err) {
        return err.response
      }
    }
  }
}

export default cart
